import { CommonModule } from '@angular/common';
import { DateTime } from 'luxon';
import { ChangeDetectorRef, Component, DestroyRef, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { environment } from 'src/environments/environment';
import { AuthenticationService } from 'src/app/authentication/authentication.service';
import { AiHulpModalComponent } from 'src/app/modules/general/modals/ai-hulp/ai-hulp.modal';
import { SilenceDurationComponent } from 'src/app/modules/general/modals/silence-duration/silence-duration.component';
import { RingComponent } from '../ring/ring.component';
import { RingService } from 'src/app/modules/general/services/ring.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatBadgeModule } from '@angular/material/badge';
import { AccountService } from 'src/app/modules/api';
import { AccountNotification } from 'src/app/models/account.model';
import { ApiFilter } from 'src/app/models/apiParams.model';
import { LuxonDateFormatPipe } from 'src/app/modules/general/pipes/luxonDateFormat.pipe';
import { NotificationStateService } from 'src/app/modules/general/services/notification-state.service';
import { NotificationSilenceService } from 'src/app/modules/general/services/notification-silence.service';
import confetti from 'canvas-confetti';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: [],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatFormFieldModule, MatInputModule, MatButtonModule, MatIconModule, MatTooltipModule, MatMenuModule, MatBadgeModule, RouterModule, FormsModule, LuxonDateFormatPipe]
})
export class AppHeaderComponent implements OnInit {
  public constructor(
    private readonly router: Router,
    private readonly authService: AuthenticationService,
    private readonly dialog: MatDialog,
    private readonly bottomSheet: MatBottomSheet,
    private readonly ringService: RingService,
    private readonly accountService: AccountService,
    private readonly notificationState: NotificationStateService,
    private readonly silenceService: NotificationSilenceService,
    private readonly cdr: ChangeDetectorRef,
    private readonly destroyRef: DestroyRef
  ) { }

  public notifications: AccountNotification[] = [];
  public unreadCount = 0;
  public lookupText: string = '';

  public get notifBadgeCount(): number {
    return this.notificationState.currentCount;
  }

  public get isSilenced(): boolean {
    return this.silenceService.isSilenced;
  }

  public toggleSilence(): void {
    if (this.isSilenced) {
      this.silenceService.deactivate();
    } else {
      this.bottomSheet.open(SilenceDurationComponent);
    }
  }

  // Press/hold tracking
  private pressTimer: number | null = null;
  private longPressThreshold: number = 500;
  private longPressActivated: boolean = false;
  private downAt: number = 0;
  private isPointerDown: boolean = false;

  public ngOnInit(): void {
    this.loadNotifications();
    this.loadUnreadCount();

    // Getters notifBadgeCount/isSilenced read service state that changes outside
    // this component; re-check the view whenever that state changes.
    this.notificationState.unreadCount
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.cdr.markForCheck());
    this.silenceService.silenced
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.cdr.markForCheck());
  }

  public loadNotifications(): void {
    if (!environment.enableNotifications) return;
    this.accountService.getNotifications(10, 1, [{ field: 'created', direction: 'DESC' }]).subscribe({
      next: (res) => {
        this.notifications = res.data as AccountNotification[];
        this.cdr.markForCheck();
      }
    });
  }

  public loadUnreadCount(): void {
    if (!environment.enableNotifications) return;
    const filter: ApiFilter = { AND: [['readAt', 'isNull']] };
    this.accountService.getNotifications(1, 1, undefined, filter).subscribe({
      next: (res) => {
        this.unreadCount = (res.paginator as { records: number } | undefined)?.records ?? 0;
        this.notificationState.setUnreadCount(this.unreadCount);
        this.cdr.markForCheck();
      }
    });
  }

  public onNotificationClick(notification: AccountNotification): void {
    const valuationId = notification.parameters?.valuationId;
    if (valuationId) {
      window.open('/taxatie/detail/' + valuationId, '_blank');
    }
    this.markAsRead(notification);
  }

  public markAsRead(notification: AccountNotification): void {
    if (notification.readAt) return;
    const readAtStr = DateTime.now().toFormat('yyyy-MM-dd HH:mm');
    this.accountService.updateNotification(notification.id, { readAt: readAtStr }).subscribe({
      next: () => {
        notification.readAt = { date: readAtStr, timezone_type: 3, timezone: 'Europe/Amsterdam' };
        this.unreadCount = Math.max(0, this.unreadCount - 1);
        this.notificationState.setUnreadCount(this.unreadCount);
        this.cdr.markForCheck();
      }
    });
  }

  public onKey(_evt?: Event): void {
    const input: string = (this.lookupText ?? '').trim();
    if (!input) {
      return;
    }

    const reNum: RegExp = /^\d+$/;
    const reTax: RegExp = /^[tT](\d+)$/;
    const reRel: RegExp = /^[cC](\d+)$/;
    const reEmp: RegExp = /^[eE](\d+)$/;
    const rePer: RegExp = /^[pP](\d+)$/;
    const reAlpha: RegExp = /^[a-zA-Z ]+$/;

    let route: string;

    if (reNum.test(input)) {
      route = `/taxatie/detail/${input}`;
    } else if (reTax.test(input)) {
      const id: string = input.match(reTax)![1];
      route = `/taxatie/detail/${id}`;
    } else if (reRel.test(input)) {
      const id: string = input.match(reRel)![1];
      route = `/crm/relatie-detail/${id}`;
    } else if (reEmp.test(input)) {
      const id: string = input.match(reEmp)![1];
      route = `/crm/werknemer-detail/${id}`;
    } else if (rePer.test(input)) {
      const id: string = input.match(rePer)![1];
      route = `/crm/persoon-detail/${id}`;
    } else if (reAlpha.test(input)) {
      route = `/crm/relaties/${encodeURIComponent(input)}`;
    } else {
      route = `/taxatie/zoeken/${encodeURIComponent(input)}`;
    }

    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.navigateByUrl(route).then(() => {
      this.router.navigated = false;
      this.router.navigate([this.router.url]);
    });

    this.lookupText = '';
  }

  public openAiModal(): void {
    const currentUrl: string = this.router.url;
    let valuationId: number | null = null;

    if (currentUrl.includes('/taxatie/detail/')) {
      const segments: string[] = currentUrl.split('/');
      valuationId = Number(segments[segments.length - 1]) || null;
    }

    this.dialog.open(AiHulpModalComponent, {
      width: '800px',
      data: { valuationId }
    });
  }

  public onPointerDown(_event: PointerEvent): void {
    this.isPointerDown = true;
    this.longPressActivated = false;
    this.downAt = Date.now();
    this.clearTimer();

    this.pressTimer = window.setTimeout((): void => {
      // Long press reached
      this.longPressActivated = true;
      this.isPointerDown = false; // consume the press
      this.dialog.open(RingComponent, {
        width: '380px',
        autoFocus: false
      });
    }, this.longPressThreshold) as unknown as number;
  }

  public onPointerUp(_event: PointerEvent): void {
    // If we already opened the dialog via long press, just ignore the release
    if (this.longPressActivated === true) {
      this.clearTimer();
      this.isPointerDown = false;
      return;
    }

    // Short press path: elapsed less than threshold
    const elapsed: number = Date.now() - this.downAt;
    this.clearTimer();
    this.isPointerDown = false;

    if (elapsed < this.longPressThreshold) {
      this.ringService.ring(1, 'hard', { light: true }).subscribe({
        next: () => {
          this.triggerConfetti();
        },
        error: (e) => console.error('Bell (short press) failed:', e)
      });
    }
  }

  public onPointerLeave(_event: PointerEvent): void {
    if (this.isPointerDown) {
      this.clearTimer();
      this.isPointerDown = false;
    }
  }

  private clearTimer(): void {
    if (this.pressTimer !== null) {
      window.clearTimeout(this.pressTimer);
      this.pressTimer = null;
    }
  }

  private triggerConfetti(): void {
    // Center burst
    confetti({ particleCount: 120, spread: 70, startVelocity: 45, origin: { y: 0.25 } });
    // Side salvos
    confetti({ particleCount: 80, angle: 60, spread: 60, origin: { x: 0, y: 0.6 } });
    confetti({ particleCount: 80, angle: 120, spread: 60, origin: { x: 1, y: 0.6 } });
    // Small follow-up sparkle
    window.setTimeout(() => {
      confetti({ particleCount: 60, spread: 80, startVelocity: 35, origin: { y: 0.3 } });
    }, 300);
  }

  public logout(): void {
    this.authService.logout();
  }
}
